import React, { useEffect, useRef, useMemo, useState } from "react";
import { Container, Button, Box, Typography, Select, MenuItem } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  REBALANCER_SUMMARY as summaryHeader,
  TABLE_CROSS_TRADE as tableHeader,
  findFundAbbById,
} from "../../../../component/Methods";

import { useDispatch, useSelector } from "react-redux";
import CrossingTradesTable from "./components/CrossingTradesTable";
import {
  addActiveRequests,
  updateLoading,
  updateLoadingMsg,
} from "../../../../Redux/Reducers/uiSlice";
import SummaryTable from "./components/SummaryTable";
import { sendWebSocketMessage } from "../../../../hooks/useWebSocket";
import {
  selectAllTrades,
  selectFinalCrossedTrades,
  selectProcessId,
  selectFunds,
} from "../../../../Redux/Reducers/selectors";

const FundStep6 = ({ rebalancedFunds, rebalanceType }) => {
  const AllTrades = useSelector(selectAllTrades);
  const finalCrossedTrades = useSelector(selectFinalCrossedTrades);
  const processId = useSelector(selectProcessId);
  const funds = useSelector(selectFunds);
  const crossingTradesTableRef = useRef();
  const [selectedFund, setSelectedFund] = useState("All");

  const dispatch = useDispatch();

  const fundOptions = useMemo(() => {
    if (!finalCrossedTrades?.length) return [{ id: "All", abbreviation: "All" }];

    const unique = finalCrossedTrades
      .map((order) => ({
        id: order?.fund,
        abbreviation: findFundAbbById(order?.fund, funds),
      }))
      .filter(
        (value, index, self) =>
          value.id &&
          index ===
            self.findIndex(
              (t) => t.id === value.id && t.abbreviation === value.abbreviation
            )
      );

    return [{ id: "All", abbreviation: "All" }, ...unique];
  }, [finalCrossedTrades, funds]);

  const filteredCrossedTrades = useMemo(() => {
    if (!finalCrossedTrades?.length) return [];
    if (selectedFund === "All") return finalCrossedTrades;
    return finalCrossedTrades.filter((order) => order?.fund == selectedFund);
  }, [finalCrossedTrades, selectedFund]);

  useEffect(() => {
    if (
      rebalanceType === "all" &&
      processId &&
      processId != "" &&
      rebalancedFunds &&
      rebalanceType === "all"
    ) {
      sendWebSocketMessage(
        JSON.stringify({
          action: "rebalancer_summary",
          data: {
            processId: processId,
            fundIds: rebalancedFunds,
            rebalancerType: 1,
          },
        })
      );
      dispatch(updateLoadingMsg("Generating Summary"));
      dispatch(updateLoading(true));
    }
  }, [processId, dispatch]);
  const onNext = () => {
    const ignoreList =
      crossingTradesTableRef.current?.getIgnoredTradeIds() || [];

    const socketRequest = {
      action: "finalize_rebalancer_orders",
      data: {
        fundIds: rebalancedFunds,
        processId: rebalanceType === "all" ? processId : AllTrades?.processId,
        rebalancerType: rebalanceType === "all" ? 1 : 0,
        ignoredTrades: ignoreList,
      },
    };

    sendWebSocketMessage(JSON.stringify(socketRequest));
    dispatch(addActiveRequests("finalize_rebalancer_orders"));
    dispatch(updateLoadingMsg("Generating Trades"));
    dispatch(updateLoading(true));
  };

  return (
    <Container
      disableGutters
      maxWidth="100%"
      sx={{
        marginTop: 2,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        width: "100%",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            flexShrink: 0,
          }}
        >
          <Box sx={{ width: "44%" }}>
            <Box
              sx={{
                marginBottom: 1,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{
                  fontWeight: "bold !important",
                  fontSize: "20px !important",
                }}
              >
                Final Trades
              </Typography>
              {finalCrossedTrades && finalCrossedTrades.length > 0 ? (
                <Select
                  value={selectedFund}
                  onChange={(e) => setSelectedFund(e.target.value)}
                  sx={{ marginLeft: 2 }}
                  size="small"
                >
                  {fundOptions.map((fund) => (
                    <MenuItem key={fund.id} value={fund.id}>
                      {fund.abbreviation}
                    </MenuItem>
                  ))}
                </Select>
              ) : null}
            </Box>
          </Box>
          {finalCrossedTrades && finalCrossedTrades.length > 0 ? (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={onNext}
                sx={{ width: "200px", height: "40px" }}
              >
                Send To Blotter
              </Button>
            </Box>
          ) : null}
        </Box>

        {finalCrossedTrades ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              width: "100%",
              flex: 1,
              minHeight: 0,
              gap: 2,
            }}
          >
            <Box
            sx={{
              width: "47%",
              flexShrink: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
            >
              <CrossingTradesTable
                ref={crossingTradesTableRef}
                crossingTrades={filteredCrossedTrades}
                tableHeader={tableHeader}
              />
            </Box>
            <Box
            sx={{
              width: "52%",
              flexShrink: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
            >
              <SummaryTable tableHeader={summaryHeader} />
            </Box>
          </Box>
        ) : (
          <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
            No data available
          </Typography>
        )}
      </Box>
    </Container>
  );
};

export default FundStep6;
